"use client"

import { useState } from "react"
import { AlertTriangle, CheckCircle2 } from "lucide-react"

import { cn } from "@/common/lib/utils"
import type { CoverageRow } from "@/modules/lernpfad/engine/aggregate"
import type { Translator } from "@/modules/lernpfad/i18n/ui"
import { MARK_RADIUS, STATUS_VAR } from "./palette"

/**
 * Mandatory-training coverage per obligation, against a target.
 *
 * Colour job here is STATUS (compliant / below target), not identity — so it
 * uses the reserved status palette and ships with an icon and a label, never
 * colour alone. The target rule turns "89%" from a number into a judgement the
 * reader can make without knowing the policy.
 */

interface CoverageChartProps {
    rows: CoverageRow[]
    t: Translator
    obligationLabel: (id: string, fallback: string) => string
    obligationBasis: (id: string, fallback: string) => string
    /** Locale-aware number formatter, supplied by the active localizer. */
    num: (value: number) => string
    /** Compliance target the bars are read against. */
    target?: number
}

export default function CoverageChart({
    rows,
    t,
    obligationLabel,
    obligationBasis,
    num,
    target = 0.95,
}: CoverageChartProps) {
    const [hover, setHover] = useState<string | null>(null)

    return (
        <figure className="m-0 space-y-2.5">
            {rows.map((row) => {
                const belowTarget = row.coverage < target
                const Icon = belowTarget ? AlertTriangle : CheckCircle2
                const color = belowTarget ? STATUS_VAR.warning : STATUS_VAR.good
                const isHovered = hover === row.obligationId

                return (
                    <div
                        key={row.obligationId}
                        className={cn(
                            "rounded-md px-2 py-1.5 transition-colors",
                            isHovered && "bg-muted/60",
                        )}
                        onMouseEnter={() => setHover(row.obligationId)}
                        onMouseLeave={() => setHover(null)}
                    >
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <span className="flex items-center gap-1.5 text-sm font-medium">
                                <Icon className="size-3.5 shrink-0" style={{ color }} />
                                {obligationLabel(row.obligationId, row.label)}
                            </span>
                            <span className="text-xs tabular-nums text-muted-foreground">
                                {num(row.compliant)} /{" "}
                                {num(row.bound)}
                                <span className="ml-1.5 font-semibold" style={{ color }}>
                                    {Math.round(row.coverage * 100)}%
                                </span>
                            </span>
                        </div>

                        <div className="relative mt-1.5 h-2.5">
                            {/* Track */}
                            <div
                                className="absolute inset-0 rounded-full"
                                style={{ background: STATUS_VAR.track }}
                            />
                            {/* Value */}
                            <div
                                className="absolute inset-y-0 left-0 transition-all"
                                style={{
                                    width: `${row.coverage * 100}%`,
                                    background: color,
                                    borderRadius: MARK_RADIUS,
                                }}
                            />
                            {/* Target rule */}
                            <div
                                className="absolute inset-y-[-2px] w-px"
                                style={{ left: `${target * 100}%`, background: "currentColor", opacity: 0.45 }}
                                title={`${Math.round(target * 100)}%`}
                            />
                        </div>

                        <p className="mt-1 text-[11px] text-muted-foreground">
                            {obligationBasis(row.obligationId, row.basis)}
                            {row.overdue > 0 && (
                                <span style={{ color: STATUS_VAR.critical }}>
                                    {" · "}
                                    {t("hr.overdueSuffix", { count: num(row.overdue) })}
                                </span>
                            )}
                        </p>
                    </div>
                )
            })}

            <figcaption className="flex items-center gap-4 pt-1 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="size-3" style={{ color: STATUS_VAR.good }} />
                    ≥ {Math.round(target * 100)}%
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <AlertTriangle className="size-3" style={{ color: STATUS_VAR.warning }} />
                    &lt; {Math.round(target * 100)}%
                </span>
            </figcaption>
        </figure>
    )
}
